import type { ProjectDetails, ProjectStatus, ProjectPriority, BillingType, ProjectTeamMember, TimeEntryCode } from '@/types/project';
import { addDays, subDays, addMonths, subMonths } from 'date-fns';

const projectManagers = [
  { name: 'Sarah Mitchell', email: 'sarah.mitchell@timetrackpro.com' },
  { name: 'James Anderson', email: 'james.anderson@timetrackpro.com' },
  { name: 'Emma Thompson', email: 'emma.thompson@timetrackpro.com' },
  { name: 'Michael Roberts', email: 'michael.roberts@timetrackpro.com' },
  { name: 'Sophie Williams', email: 'sophie.williams@timetrackpro.com' },
];

const teamRoles = [
  'Senior Developer', 'Developer', 'Business Analyst', 'QA Engineer', 
  'DevOps Engineer', 'UX Designer', 'Technical Architect', 'Scrum Master'
];

const projectPhases = [
  'Discovery', 'Design', 'Development', 'Testing', 'Deployment', 'Support'
];

const commonTimeEntryCodes: TimeEntryCode[] = [
  { id: 'te-dev', code: 'DEV', name: 'Development', billable: true },
  { id: 'te-test', code: 'TEST', name: 'Testing', billable: true },
  { id: 'te-pm', code: 'PM', name: 'Project Management', billable: true },
  { id: 'te-meet', code: 'MEET', name: 'Client Meetings', billable: true },
  { id: 'te-doc', code: 'DOC', name: 'Documentation', billable: true },
  { id: 'te-train', code: 'TRAIN', name: 'Training', billable: false },
  { id: 'te-support', code: 'SUP', name: 'Support', billable: true },
  { id: 'te-admin', code: 'ADMIN', name: 'Administration', billable: false },
];

const generateTeamMembers = (count: number): ProjectTeamMember[] => {
  const firstNames = ['James', 'Sarah', 'Michael', 'Emma', 'David', 'Sophie', 'Robert', 'Emily', 'John', 'Jessica'];
  const lastNames = ['Smith', 'Jones', 'Williams', 'Brown', 'Taylor', 'Davies', 'Wilson', 'Evans'];
  
  const members: ProjectTeamMember[] = [];
  for (let i = 0; i < count; i++) {
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    const name = `${firstName} ${lastName}`;
    const role = teamRoles[Math.floor(Math.random() * teamRoles.length)];
    const hasPhoto = Math.random() < 0.7;
    
    members.push({
      id: `tm-${i + 1}`,
      name,
      role,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@timetrackpro.com`,
      photoUrl: hasPhoto ? `https://picsum.photos/seed/${name}-${i}/300/300` : undefined,
      allocation: Math.floor(Math.random() * 6) * 10 + 50, // 50-100% in 10% increments
      startDate: subDays(new Date(), Math.floor(Math.random() * 90)),
      endDate: Math.random() > 0.3 ? addDays(new Date(), Math.floor(Math.random() * 180)) : undefined,
    });
  }
  
  return members;
};

export const generateProjectsData = (): ProjectDetails[] => {
  const clients = [
    { name: 'Barclays Bank', sector: 'enterprise' as const, contact: 'John Davies', email: 'john.davies@barclays.com' },
    { name: 'HSBC Holdings', sector: 'enterprise' as const, contact: 'Sarah Chen', email: 'sarah.chen@hsbc.com' },
    { name: 'NHS Digital', sector: 'public-sector' as const, contact: 'Dr. Emily Watson', email: 'emily.watson@nhs.net' },
    { name: 'Monzo Bank', sector: 'mid-market' as const, contact: 'Alex Turner', email: 'alex.turner@monzo.com' },
    { name: 'Octopus Energy', sector: 'mid-market' as const, contact: 'Rachel Green', email: 'rachel.green@octopus.energy' },
    { name: 'Transport for London', sector: 'public-sector' as const, contact: 'Michael Brown', email: 'michael.brown@tfl.gov.uk' },
    { name: 'Aviva Insurance', sector: 'enterprise' as const, contact: 'David Wilson', email: 'david.wilson@aviva.com' },
    { name: 'Deliveroo', sector: 'mid-market' as const, contact: 'Sophie Martin', email: 'sophie.martin@deliveroo.com' },
    { name: 'Ministry of Defence', sector: 'public-sector' as const, contact: 'Col. James Anderson', email: 'james.anderson@mod.gov.uk' },
    { name: 'Tesco PLC', sector: 'enterprise' as const, contact: 'Emma Thompson', email: 'emma.thompson@tesco.com' },
  ];

  const projectTypes = [
    {
      name: 'Cloud Migration',
      description: 'Migration of legacy systems to cloud infrastructure',
      objectives: ['Migrate core systems to AWS', 'Reduce infrastructure costs by 40%', 'Improve system reliability'],
      deliverables: ['Migration strategy document', 'Migrated applications', 'Performance testing report'],
    },
    {
      name: 'Digital Transformation',
      description: 'End-to-end digital transformation initiative',
      objectives: ['Modernize customer-facing applications', 'Implement agile practices', 'Enhance user experience'],
      deliverables: ['Digital strategy', 'Modernized applications', 'Training materials'],
    },
    {
      name: 'API Integration Platform',
      description: 'Development of enterprise API integration platform',
      objectives: ['Build scalable API gateway', 'Integrate with 20+ systems', 'Ensure 99.9% uptime'],
      deliverables: ['API gateway', 'Integration documentation', 'Monitoring dashboard'],
    },
    {
      name: 'Cyber Security Enhancement',
      description: 'Comprehensive security assessment and remediation',
      objectives: ['Conduct security audit', 'Implement zero-trust architecture', 'Achieve ISO 27001 compliance'],
      deliverables: ['Security audit report', 'Remediation plan', 'Security controls implementation'],
    },
    {
      name: 'Data Analytics Platform',
      description: 'Enterprise data analytics and business intelligence platform',
      objectives: ['Centralize data from multiple sources', 'Enable self-service analytics', 'Improve decision-making'],
      deliverables: ['Data warehouse', 'BI dashboards', 'User training program'],
    },
    {
      name: 'Mobile App Development',
      description: 'Native mobile application for iOS and Android',
      objectives: ['Launch customer mobile app', 'Achieve 4.5+ app store rating', 'Support 100k+ users'],
      deliverables: ['iOS app', 'Android app', 'Backend API', 'App store listings'],
    },
    {
      name: 'DevOps Transformation',
      description: 'Implementation of DevOps practices and CI/CD pipelines',
      objectives: ['Automate deployment processes', 'Reduce deployment time by 80%', 'Improve code quality'],
      deliverables: ['CI/CD pipelines', 'Infrastructure as code', 'DevOps playbook'],
    },
    {
      name: 'Legacy System Modernization',
      description: 'Modernization of legacy mainframe applications',
      objectives: ['Migrate from mainframe to microservices', 'Improve system performance', 'Reduce maintenance costs'],
      deliverables: ['Modernized applications', 'Migration documentation', 'Training materials'],
    },
  ];

  const projects: ProjectDetails[] = [];
  let projectId = 1;

  clients.forEach((client, clientIndex) => {
    // Each client has 2-4 projects
    const projectCount = Math.floor(Math.random() * 3) + 2;
    
    for (let i = 0; i < projectCount; i++) {
      const projectType = projectTypes[Math.floor(Math.random() * projectTypes.length)];
      const pm = projectManagers[Math.floor(Math.random() * projectManagers.length)];
      const teamSize = Math.floor(Math.random() * 15) + 5; // 5-20 team members
      
      // Determine status with realistic distribution
      let status: ProjectStatus;
      const statusRand = Math.random();
      if (statusRand < 0.7) status = 'active';
      else if (statusRand < 0.85) status = 'at-risk';
      else status = 'on-hold';
      
      // Determine priority
      let priority: ProjectPriority;
      const priorityRand = Math.random();
      if (priorityRand < 0.3) priority = 'high';
      else if (priorityRand < 0.8) priority = 'medium';
      else priority = 'low';
      
      // Determine billing type
      let billingType: BillingType;
      const billingRand = Math.random();
      if (billingRand < 0.4) billingType = 'time-and-materials';
      else if (billingRand < 0.7) billingType = 'fixed-price';
      else if (billingRand < 0.9) billingType = 'retainer';
      else billingType = 'non-billable';
      
      const startDate = subMonths(new Date(), Math.floor(Math.random() * 12) + 1);
      const endDate = addMonths(startDate, Math.floor(Math.random() * 12) + 6);
      
      // Generate budget
      const totalHours = teamSize * 160 * 6; // 6 months average
      const usedHours = Math.floor(totalHours * (Math.random() * 0.5 + 0.2)); // 20-70% used
      const remainingHours = totalHours - usedHours;
      
      // Calculate scheduled hours (future allocated work)
      // Scheduled hours can be: under-utilized, well-balanced, or over-scheduled
      const scheduledRatio = Math.random();
      let scheduledHours: number;
      if (scheduledRatio < 0.3) {
        // Under-utilized: scheduled < 60% of remaining
        scheduledHours = Math.floor(remainingHours * (Math.random() * 0.4 + 0.2)); // 20-60%
      } else if (scheduledRatio < 0.7) {
        // Well-balanced: scheduled 60-90% of remaining
        scheduledHours = Math.floor(remainingHours * (Math.random() * 0.3 + 0.6)); // 60-90%
      } else {
        // Over-scheduled: scheduled > 90% of remaining (potentially at risk)
        scheduledHours = Math.floor(remainingHours * (Math.random() * 0.3 + 0.9)); // 90-120%
      }
      
      const hourlyRate = 850; // £850 per day / 7.5 hours
      
      const code = `${client.name.substring(0, 3).toUpperCase()}-${String(projectId).padStart(4, '0')}`;
      
      projects.push({
        id: `project-${projectId}`,
        code,
        name: projectType.name,
        description: projectType.description,
        
        clientId: `client-${clientIndex + 1}`,
        clientName: client.name,
        clientSector: client.sector,
        clientContactName: client.contact,
        clientContactEmail: client.email,
        
        status,
        priority,
        
        timeline: {
          startDate,
          endDate,
          currentPhase: projectPhases[Math.floor(Math.random() * projectPhases.length)],
          milestones: [
            {
              id: `ms-${projectId}-1`,
              name: 'Project Kickoff',
              dueDate: addDays(startDate, 7),
              status: 'completed',
              description: 'Initial project setup and team onboarding',
            },
            {
              id: `ms-${projectId}-2`,
              name: 'Design Phase Complete',
              dueDate: addMonths(startDate, 2),
              status: new Date() > addMonths(startDate, 2) ? 'completed' : 'in-progress',
              description: 'Complete all design documentation and approvals',
            },
            {
              id: `ms-${projectId}-3`,
              name: 'Development Complete',
              dueDate: addMonths(startDate, 5),
              status: new Date() > addMonths(startDate, 5) ? 'completed' : 'pending',
              description: 'All development work completed and code reviewed',
            },
            {
              id: `ms-${projectId}-4`,
              name: 'Go-Live',
              dueDate: endDate,
              status: 'pending',
              description: 'Production deployment and go-live',
            },
          ],
        },
        
        projectManager: pm.name,
        projectManagerEmail: pm.email,
        teamMembers: generateTeamMembers(teamSize),
        teamSize,
        
        billingType,
        budget: {
          totalHours,
          usedHours,
          remainingHours,
          scheduledHours,
          totalCost: totalHours * hourlyRate,
          usedCost: usedHours * hourlyRate,
          remainingCost: remainingHours * hourlyRate,
          currency: 'GBP',
        },
        billable: billingType !== 'non-billable',
        
        timeEntryCodes: commonTimeEntryCodes,
        requiresDescription: true,
        requiresApproval: priority === 'high' || client.sector === 'public-sector',
        allowWeekendEntry: false,
        maxDailyHours: 12,
        
        objectives: projectType.objectives,
        deliverables: projectType.deliverables,
        specialInstructions: priority === 'high' 
          ? 'All time entries require detailed descriptions. Weekend work must be pre-approved by project manager.'
          : 'Standard time entry procedures apply.',
        tags: [
          client.sector,
          billingType,
          projectType.name.split(' ')[0].toLowerCase(),
        ],
        
        createdAt: subDays(startDate, 30),
        updatedAt: subDays(new Date(), Math.floor(Math.random() * 7)),
        lastActivityAt: subDays(new Date(), Math.floor(Math.random() * 3)),
      });
      
      projectId++;
    }
  });

  return projects;
};

export const mockProjects = generateProjectsData();
